// Created by Claude Code AI Agent
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ToastController } from '@ionic/angular/lazy';
import { NavigationTabs } from 'src/app/shared/navigation-tabs';
import { WarehouseAccessResult, WarehouseAccessService } from 'src/app/shared/warehouse-access.service';
import { presentWarehouseToast } from 'src/app/pages/warehouse/warehouse-failure-toast';

// 'unknown' names no cause — the check itself failed — but silence read as the app ignoring the tap.
const DENIAL_MESSAGES: Record<Exclude<WarehouseAccessResult, 'granted'>, string> = {
  'no-permission': 'Unable to open the warehouse. Your role does not have warehouse access. Ask the office to enable permissions.',
  'not-enabled': 'Unable to open the warehouse. Warehouse management is not turned on for your company. Ask the office to enable it.',
  unknown: 'Unable to open the warehouse. Please try again. If it keeps happening, let the office know.'
};

// Protects the Warehouse route on direct URL entry (the tab button can be hidden while the
// URL is still reachable). Awaits the same live check as the tab button, so the route and the
// tab are always consistent; redirects unauthorized users back to the schedule view.
export const warehouseAccessGuard: CanActivateFn = async () => {
  const warehouseAccess = inject(WarehouseAccessService);
  const toastController = inject(ToastController);
  const router = inject(Router);

  // Fail safe: only isWarehouseEnabled() is guarded inside the service, so a rejection anywhere else would
  // surface as a router NavigationError and leave the tap doing nothing at all.
  const result = await warehouseAccess.getAccessResult().catch((): WarehouseAccessResult => 'unknown');

  if (result === 'granted') {
    return true;
  }

  // Not awaited: the redirect should not wait out the toast's enter animation, and a rejection here would
  // reject the guard itself and strand the tap as a NavigationError.
  void presentWarehouseToast(toastController, DENIAL_MESSAGES[result]).catch(() => undefined);

  return router.createUrlTree([NavigationTabs.ScheduleView]);
};
